// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The controls, drawn as they are being used (14 Sep 2026).
//
// `CabinetRig` has computed a depression for every button since M5 — "the depression each control
// should be drawn at, 0 … 1" — and, like the plunger before M8, nothing drew it: "The buttons do
// not show being depressed", from the product owner on a Quest 3, the round the start button
// reached the front door. The same round asked for the other half of the picture: "add an
// indicator that lets the user know their hand is positioned on the flipper properly … a little
// green light … close to the side of flipper". A flipper button is on the cabinet's flank, below
// the rail, out of the eye's way while it watches the ball; whether a finger is in its zone is
// exactly what the player cannot see and exactly what the gate knows.
//
// So this reads the rig every frame and moves the picture: each cap sinks `CAP_TRAVEL` along its
// own press axis by its depression; the lamp `cabinet.ts` sets into the rail above each flipper
// button goes green while a fingertip is in that button's zone and white while the button is down;
// the start button — lit, as a real one is — burns brighter while it is pressed; and the shooter
// rod — tip, rod and knob, one piece — slides along its own axis by however far the player has
// drawn it (this was `XRRuntime.pullThePlunger`, which slid two parts along z; the rod now lies
// along the lane, which is tilted, and has three parts). Found by name under the cabinet's group,
// lazily, because the cabinet is built by the world and this is handed the group after. Nothing
// here decides anything; the rig decided, this shows it.

import { Color, type Mesh, type MeshStandardMaterial, type Object3D } from 'three'
import {
  GAUGE_MARKER,
  GAUGE_OFF,
  KNOB_COOL,
  PRESSABLE_IDS,
  PULL_GAUGE_SEGMENTS,
  PullGauge,
  gaugeColour,
  knobColour,
  plungerSpringLength,
  plungerSpringOf,
  plungerTension,
  type CabinetControls,
  type PressableId,
} from '../xr/cabinetControls'

/** How far a cap sinks when fully pressed, metres. A real button travels about this. */
export const CAP_TRAVEL = 0.005

/** The rail lamp: dark until a fingertip is in the zone, green while it is, white while pressed. */
export const LAMP_OFF = 0x1c261c
export const LAMP_OVER = 0x39ff6b
export const LAMP_PRESSED = 0xf4fff6
/** The lamp's own light, in the same three states. */
export const LAMP_GLOW_OFF = 0
export const LAMP_GLOW_OVER = 1.4
export const LAMP_GLOW_PRESSED = 2

/** The start button's light at rest and while pressed; `cabinet.ts` starts it at rest. */
export const START_GLOW_REST = 0.22
export const START_GLOW_PRESSED = 0.32

/** The knob's light: off, green-lit while a hand is in the grab zone, brighter while held. */
export const KNOB_GLOW_OFF = 0
export const KNOB_GLOW_OVER = 0.9
export const KNOB_GLOW_HELD = 1.6

/** The names `cabinet.ts` gives the lamps, by the flipper they watch. */
export const LAMP_NAMES: Partial<Record<PressableId, string>> = {
  flipperLeft: 'lampFlipperLeft',
  flipperRight: 'lampFlipperRight',
}

/** The names `cabinet.ts` gives the three parts of the shooter rod. They slide together. */
export const PLUNGER_PART_NAMES = ['plungerTip', 'plungerRod', 'plungerKnob'] as const

/** The pull gauge's light: a lit lamp, and the last shot's marker (26 Sep 2026). */
export const GAUGE_GLOW_LIT = 1.4
export const GAUGE_GLOW_MARKER = 0.6

/** What the picture reads off the rig each frame. */
export interface ControlReadout {
  depressionOf: (id: PressableId) => number
  overOf: (id: PressableId) => boolean
  /** How far the shooter rod is drawn, metres along its axis, 0 at rest. */
  readonly plungerDrawn: number
  /** Is a hand in the rod's grab zone, or holding it? The knob's light (14 Sep 2026). */
  readonly plungerOver: boolean
  /** Is the rod held? */
  readonly plungerHeld: boolean
}

interface Placed {
  readonly mesh: Object3D
  readonly restX: number
  readonly restY: number
  readonly restZ: number
  readonly along: { readonly x: number; readonly y: number; readonly z: number }
}

const colour = new Color()

export class ControlPicture {
  private readonly root: Object3D
  private readonly controls: CabinetControls
  private readonly caps = new Map<PressableId, Placed>()
  private readonly lamps = new Map<PressableId, Mesh>()
  private readonly plunger: Placed[] = []
  private knob: Mesh | undefined
  private spring: Object3D | undefined
  private springRest = 0
  private readonly gaugeLamps: Mesh[] = []
  private readonly gauge = new PullGauge()
  private searched = false

  constructor(root: Object3D, controls: CabinetControls) {
    this.root = root
    this.controls = controls
  }

  /** What the search found, for the hands line: `n caps, n lamps, n rod parts`. */
  get found(): string {
    return `${this.caps.size} caps, ${this.lamps.size} lamps, ${this.plunger.length} rod parts`
  }

  /** Move every cap, light every lamp and slide the rod to what the rig says this frame. */
  draw(read: ControlReadout): void {
    if (!this.searched) this.find()
    for (const id of PRESSABLE_IDS) {
      const depression = read.depressionOf(id)
      const cap = this.caps.get(id)
      if (cap !== undefined) {
        slide(cap, depression * CAP_TRAVEL)
        if (id === 'start') {
          const material = (cap.mesh as Mesh).material as MeshStandardMaterial
          if (material.emissiveIntensity !== undefined) {
            material.emissiveIntensity = depression > 0 ? START_GLOW_PRESSED : START_GLOW_REST
          }
        }
      }
      const lamp = this.lamps.get(id)
      if (lamp !== undefined) {
        const down = depression > 0
        const over = down || read.overOf(id)
        const material = lamp.material as MeshStandardMaterial
        colour.setHex(down ? LAMP_PRESSED : over ? LAMP_OVER : LAMP_OFF)
        material.color.copy(colour)
        material.emissive.copy(colour)
        material.emissiveIntensity = down ? LAMP_GLOW_PRESSED : over ? LAMP_GLOW_OVER : LAMP_GLOW_OFF
      }
    }
    // No negation: Three.js is handed the core's frame, where the cabinet's front is +z and the
    // axis points at the player; only `MeshUpload.ToUnity` flips it. A minus here would drive the
    // rod into the machine.
    const drawn = read.plungerDrawn
    for (const part of this.plunger) slide(part, drawn)
    const tension = plungerTension(drawn)
    const knob = this.knob
    if (knob !== undefined) {
      const material = knob.material as MeshStandardMaterial
      const glow=read.plungerHeld ? KNOB_GLOW_HELD : read.plungerOver ? KNOB_GLOW_OVER : KNOB_GLOW_OFF
      // Authored dark polymer retains its reflected highlights while the original controls signal a grab.
      material.emissiveIntensity = glow*(knob.userData['componentPlunger']===true?.035:1)
      // The knob warms with the pull while it is held (26 Sep 2026): green over the grab zone,
      // toward orange at a full draw.
      colour.setHex(read.plungerHeld ? knobColour(tension) : KNOB_COOL)
      material.emissive.copy(colour)
    }
    // The spring is anchored at the panel and stretched by the knob: its y is its length.
    if (this.spring !== undefined && this.springRest > 0) {
      const update=this.spring.userData['componentPlungerDraw']
      if(typeof update==='function')update(drawn)
      else this.spring.scale.y = plungerSpringLength(this.springRest, drawn) / this.springRest
    }
    if (this.gaugeLamps.length > 0) {
      const reading = this.gauge.update(tension)
      this.gaugeLamps.forEach((lamp, i) => {
        const material = lamp.material as MeshStandardMaterial
        const lit = i < reading.lit
        const marked = !lit && i === reading.marker
        colour.setHex(lit ? gaugeColour(i) : marked ? GAUGE_MARKER : GAUGE_OFF)
        material.color.copy(colour)
        material.emissive.copy(colour)
        material.emissiveIntensity = lit ? GAUGE_GLOW_LIT : marked ? GAUGE_GLOW_MARKER : 0
      })
    }
  }

  /**
   * The caps, lamps and rod parts by name, under the cabinet's own group; searched once they
   * exist, and once only. **Under the cabinet, not the machine**: the playfield's flipper devices
   * are named `flipperLeft` and `flipperRight` too (`deviceMeshes.ts` names a device's group by
   * its id), and a search from the machine's root finds the bat before the button and sinks the
   * wrong one.
   */
  private find(): void {
    const cabinet = this.root.name === 'cabinet' ? this.root : this.root.getObjectByName('cabinet')
    if (cabinet === undefined) return
    let found = 0
    for (const id of PRESSABLE_IDS) {
      const mesh = cabinet.getObjectByName(id)
      if (mesh !== undefined) {
        found += 1
        this.caps.set(id, placed(mesh, this.controls[id].inward))
      }
      const lampName = LAMP_NAMES[id]
      if (lampName !== undefined) {
        const lamp = cabinet.getObjectByName(lampName)
        if (lamp !== undefined) this.lamps.set(id, lamp as Mesh)
      }
    }
    for (const name of PLUNGER_PART_NAMES) {
      const mesh = cabinet.getObjectByName(name)
      if (mesh !== undefined) this.plunger.push(placed(mesh, this.controls.plunger.axis))
      if (name === 'plungerKnob' && mesh !== undefined) this.knob = mesh as Mesh
    }
    this.spring = cabinet.getObjectByName('plungerSpring')
    this.springRest = plungerSpringOf(this.controls.plunger)?.rest ?? 0
    for (let i = 0; i < PULL_GAUGE_SEGMENTS; i++) {
      const lamp = cabinet.getObjectByName(`pullGauge${i}`)
      if (lamp !== undefined) this.gaugeLamps.push(lamp as Mesh)
    }
    // Searched for good once any cap exists; a machine with no cabinet yet is searched again.
    this.searched = found > 0
  }
}

function placed(mesh: Object3D, along: { readonly x: number; readonly y: number; readonly z: number }): Placed {
  return { mesh, restX: mesh.position.x, restY: mesh.position.y, restZ: mesh.position.z, along }
}

/** Put a part `by` metres along its own axis from where it rests. */
function slide(part: Placed, by: number): void {
  part.mesh.position.set(
    part.restX + part.along.x * by,
    part.restY + part.along.y * by,
    part.restZ + part.along.z * by,
  )
}
